/**
 * Checkout.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */


import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useCart } from '../hooks/useCart';
import { useAuth } from '../context/AuthContext';
import { useToast } from './ToastContainer';
import { Link, useNavigate } from 'react-router-dom';
import { getProductImage } from '../utils/imageMapping';
import { getIcon } from '../utils/icons';
import AddressForm from './AddressForm';
import LoadingSpinner from './LoadingSpinner';
import { getTaxRate, isPointOfSaleRebateEligible } from '../utils/taxConfig';
import { getLineUnitPriceCents } from '../utils/cartPricing';
import { isDigitalAlbum, isBillableForShipping } from '../utils/shippingConfig';
import {
  getStateProvinceValue,
  hasProfileShippingAddress,
  normalizeAddress,
  profileToShippingAddress,
  resolveCountryCode,
} from '../utils/addressUtils';
import { saveCustomerCountry } from '../utils/customerCountry';
import { formatDualCurrency, referenceCurrencyNote, showsReferenceCurrency } from '../utils/currencyUtils';
import { useCurrencyRates } from '../hooks/useCurrencyRates';
import BundleContentsToggle from './BundleContentsToggle';
import { isEmailVerified } from '../utils/emailVerified';
import {
  resolvePaymentSandboxFlags,
  buildPaymentModeHeaders,
  getStoredAdminStripeMode,
  getStoredAdminPayPalMode,
  setStoredAdminStripeMode,
  setStoredAdminPayPalMode,
  clearStoredAdminStripeMode,
  clearStoredAdminPayPalMode,
  clearLegacyPaymentModeStorage,
  providerModesAvailable,
  normalizeAvailableProviderModes,
} from '../utils/paymentSandbox';
import PaymentModeSwitch from './PaymentModeSwitch';
import AdminMailOrderModal from './AdminMailOrderModal';
import { buildMailOrderDraftFromCheckout } from '../utils/mailOrderDraft';
import { sendMailOrderInvoiceEmail } from '../utils/mailOrderInvoiceEmail';
import { MAIL_ORDER_ACTION_LABEL } from '../utils/mailOrderConstants';
import { useLanguage } from '../context/LanguageContext';
import { fmt } from '../i18n/format';
import './Checkout.css';

function flatRateBreakdownHasTierChanges(perItemBreakdown = []) {
  return perItemBreakdown.some((b) => {
    const tiers = b.tierBreakdown;
    if (!tiers || tiers.length < 2) return false;
    return tiers.some((t, i) => i > 0 && t.perItemCents !== tiers[i - 1].perItemCents);
  });
}

function formatFlatRateItemLabel(b) {
  if (b.tierBreakdown?.length) {
    let prevRate = null;
    const tierDesc = b.tierBreakdown
      .map((t) => {
        const segment = `${t.count}×$${(t.perItemCents / 100).toFixed(2)}`;
        const rateChanged = prevRate !== null && t.perItemCents !== prevRate;
        prevRate = t.perItemCents;
        return rateChanged ? `${segment}*` : segment;
      })
      .join(' + ');
    return `${b.type}: ${tierDesc}`;
  }
  const perItem = b.qty > 0 ? Math.round((b.totalCents || 0) / b.qty) : 0;
  return `${b.type}: ${b.qty}×$${(perItem / 100).toFixed(2)}`;
}

const CHECKOUT_RETURN_ENC = encodeURIComponent('/checkout');
const loadStripe = () => {
  return new Promise((resolve) => {
    if (window.Stripe) {
      resolve(window.Stripe);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.onload = () => resolve(window.Stripe);
    document.head.appendChild(script);
  });
};

// Load PayPal SDK (reload when client ID changes)
const loadPayPal = (clientId) => {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-paypal-sdk="true"]');
    if (existing && existing.dataset.clientId === clientId && window.paypal) {
      resolve(window.paypal);
      return;
    }
    if (existing) {
      existing.remove();
      delete window.paypal;
    }
    const script = document.createElement('script');
    script.dataset.paypalSdk = 'true';
    script.dataset.clientId = clientId;
    script.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(clientId)}&currency=CAD&intent=capture&components=buttons&disable-funding=credit,card,paylater`;
    script.onload = () => resolve(window.paypal);
    script.onerror = () => reject(new Error('PayPal SDK failed to load'));
    document.head.appendChild(script);
  });
};

function paymentAuthHeaders(userIsAdmin = false, modes = {}, extra = {}) {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...buildPaymentModeHeaders(userIsAdmin, modes),
    ...extra,
  };
}

function checkoutPaymentModes(stripeMode, paypalMode) {
  return {
    stripeMode: stripeMode === 'live' || stripeMode === 'sandbox' ? stripeMode : undefined,
    paypalMode: paypalMode === 'live' || paypalMode === 'sandbox' ? paypalMode : undefined,
  };
}

const Checkout = () => {
  const navigate = useNavigate();
  const {
    items,
    products,
    clearCart,
    shipping,
    tax,
    tariff,
    getCartTotal,
    getTotalWithShippingAndTax,
    getTransactionFee,
    updateShipping,
    updateTax,
    updateTariff,
    updateQuantity,
    removeItem
  } = useCart();
  const { currentUser, isAdmin } = useAuth();
  const emailVerified = isEmailVerified(currentUser);
  const mustVerifyBeforePay = Boolean(currentUser && !emailVerified);
  const { showSuccess, showError } = useToast();
  const { t } = useLanguage();
  const c = t.shop.checkout;
  const cartUi = t.shop.cart;
  const [formData, setFormData] = useState({
    // Contact Information
    email: '',
    phone: '',
    // Shipping Name
    shippingFirstName: '',
    shippingLastName: '',
    // Payment - Pre-populated with test data
    cardNumber: '4532 1488 0343 6467',
    expiryDate: '12/28',
    cvv: '123'
  });
  const [shippingAddress, setShippingAddress] = useState({
    street: '',
    street2: '',
    city: '',
    province: '',
    state: '',
    region: '',
    postalCode: '',
    country: 'Canada',
    countryName: '',
    countryCode: 'CA',
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [currentStep, setCurrentStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [completedOrderDetails, setCompletedOrderDetails] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('stripe'); // stripe | paypal | email (test site)
  const [stripe, setStripe] = useState(null);
  const [paypalReady, setPaypalReady] = useState(false);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [adminStripeMode, setAdminStripeMode] = useState(null);
  const [adminPayPalMode, setAdminPayPalMode] = useState(null);
  const [switchingPaymentMode, setSwitchingPaymentMode] = useState(false);
  const [saveAddressToProfile, setSaveAddressToProfile] = useState(false);
  const [profileHasSavedAddress, setProfileHasSavedAddress] = useState(false);
  const [userNewsletterSubscribed, setUserNewsletterSubscribed] = useState(false);
  const [isSavingProfileAddress, setIsSavingProfileAddress] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [cartInquiryMessage, setCartInquiryMessage] = useState('');
  const [adminCartTestEmail, setAdminCartTestEmail] = useState('');
  const [isSendingCartInquiry, setIsSendingCartInquiry] = useState(false);
  const [cartInquiryReference, setCartInquiryReference] = useState('');
  const [showMailOrderModal, setShowMailOrderModal] = useState(false);
  const [mailOrderDraft, setMailOrderDraft] = useState(null);
  const paypalButtonsRenderIdRef = useRef(0);
  const userIsAdmin = isAdmin();
  // Billing always uses shipping address - no separate state needed

  const subtotal = getCartTotal();
  const transactionFeeInfo = getTransactionFee();
  const transactionFee = transactionFeeInfo.amount;
  const total = getTotalWithShippingAndTax();

  const buildShippingDetailsPayload = (countryOverride) => ({
    method: shipping.method || 'standard',
    country: countryOverride ?? shipping.country ?? '',
    totalWeightKg: shipping.info?.totalWeight || 0,
    bandLabel: shipping.info?.shippingBandLabel || null,
    isWeightBased: shipping.info?.isWeightBased || false,
    isFlatRate: shipping.info?.isFlatRate || false,
    fuelBreakdown: shipping.info?.fuelBreakdown || null,
    flatRateBreakdown: shipping.info?.flatRateBreakdown || null,
    transactionFee,
    transactionFeePct: transactionFeeInfo.pct || 0,
  });
  const currencyRates = useCurrencyRates();
  const checkoutCountryCode = resolveCountryCode(shippingAddress);
  const checkoutCountryName = shippingAddress.countryName || shippingAddress.country || '';
  const dualCheckoutTotal = formatDualCurrency(total, checkoutCountryCode, currencyRates, checkoutCountryName);
  const checkoutCurrencyNote = referenceCurrencyNote(checkoutCountryCode, currencyRates, checkoutCountryName);

  useEffect(() => {
    if (userIsAdmin && currentUser?.email) {
      setAdminCartTestEmail(currentUser.email);
    }
  }, [userIsAdmin, currentUser?.email]);

  const configSandboxFlags = resolvePaymentSandboxFlags(paymentConfig);
  // Prefer the admin-selected environment so Live hides sandbox copy immediately
  const selectedStripeMode =
    (userIsAdmin && (adminStripeMode === 'live' || adminStripeMode === 'sandbox'))
      ? adminStripeMode
      : (paymentConfig?.stripeMode || paymentConfig?.defaultStripeMode || null);
  const selectedPayPalMode =
    (userIsAdmin && (adminPayPalMode === 'live' || adminPayPalMode === 'sandbox'))
      ? adminPayPalMode
      : (paymentConfig?.paypalMode || paymentConfig?.defaultPayPalMode || null);
  const stripeKey = paymentConfig?.stripePublishableKey || '';
  const isStripeSandbox =
    selectedStripeMode === 'live' || stripeKey.includes('_live_')
      ? false
      : selectedStripeMode === 'sandbox' || stripeKey.includes('_test_') || configSandboxFlags.isStripeSandbox;
  const isPayPalSandbox =
    selectedPayPalMode === 'live'
      ? false
      : selectedPayPalMode === 'sandbox' || configSandboxFlags.isPayPalSandbox;
  const isPaymentSandbox = isStripeSandbox || isPayPalSandbox;
  const paymentsAdminOnly = Boolean(paymentConfig?.paymentsAdminOnly);
  // Public customers: Stripe/PayPal only when server allows live public checkout.
  // Sandbox or admin-only mode → non-admins get Email my cart only.
  const publicCanUseStripe = !paymentsAdminOnly && !isStripeSandbox;
  const publicCanUsePayPal = !paymentsAdminOnly && !isPayPalSandbox;
  const showStripePayment =
    Boolean(paymentConfig?.stripePublishableKey?.startsWith('pk_')) &&
    (userIsAdmin || publicCanUseStripe);
  const showPayPalPayment =
    Boolean(paymentConfig?.paypalClientId) &&
    (userIsAdmin || publicCanUsePayPal);
  // Guests / non-admins: always offer email cart when card/PayPal are hidden from them
  const showEmailCart = Boolean(
    !userIsAdmin ||
    paymentsAdminOnly ||
    isStripeSandbox ||
    isPayPalSandbox ||
    (!showStripePayment && !showPayPalPayment)
  );
  const showMailOrderCheckout = userIsAdmin;
  const hasAnyPaymentMethod = showStripePayment || showPayPalPayment || showEmailCart;

  const resolveCheckoutLine = (item) => {
    const product = products.find((p) => String(p.id) === String(item.id));
    return product ? { ...product, ...item } : item;
  };

  const isDigitalOrVirtualLine = (item) => {
    if (!item || item.isCheckoutPromo) return false;
    const line = resolveCheckoutLine(item);
    if (isDigitalAlbum(line)) return true;
    if (line.shipping_enabled === false || line.shipping_enabled === 0) return true;
    return false;
  };

  const cartIsDigitalOnly = useMemo(() => {
    if (!items.length) return false;
    const paid = items.filter((item) => !item.isCheckoutPromo);
    if (!paid.length) return false;
    return paid.every((item) => isDigitalOrVirtualLine(item) || !isBillableForShipping(resolveCheckoutLine(item)));
  }, [items, products]);

  const cartHasDigital = useMemo(() => {
    if (!items.length) return false;
    return items.some((item) => isDigitalAlbum(resolveCheckoutLine(item)));
  }, [items, products]);

  /** Physical (shippable) paid items — used with digital to decide mixed-order messaging */
  const cartHasPhysical = useMemo(() => {
    if (!items.length) return false;
    return items.some((item) => {
      if (item.isCheckoutPromo) return false;
      const line = resolveCheckoutLine(item);
      return isBillableForShipping(line) && !isDigitalAlbum(line);
    });
  }, [items, products]);

  const cartIsMixedDigitalPhysical = cartHasDigital && cartHasPhysical;

  useEffect(() => {
    clearLegacyPaymentModeStorage();
  }, []);

  useEffect(() => {
    const available = [];
    if (showStripePayment) available.push('stripe');
    if (showPayPalPayment) available.push('paypal');
    if (showEmailCart) available.push('email');
    if (available.length && !available.includes(paymentMethod)) {
      setPaymentMethod(available[0]);
    }
  }, [showStripePayment, showPayPalPayment, showEmailCart, paymentMethod]);

  const mapCartItemsForApi = () => items.map((item) => ({
    id: item.id,
    name: item.name,
    quantity: item.quantity,
    unitPrice: getLineUnitPriceCents(item),
    price_cents: getLineUnitPriceCents(item),
    price: getLineUnitPriceCents(item),
    selectedSize: item.selectedSize || null,
    selectedColor: item.selectedColor || null,
    isBundle: item.isBundle || false,
    bundleProducts: item.bundleProducts || [],
    bundleOptionName: item.bundleOptionName || null,
    bundleTaxAllocations: item.bundleTaxAllocations || null,
  }));

  const handleSendCartToPaul = async () => {
    if (!items.length) {
      showError('Your cart is empty.');
      return;
    }
    if (!formData.email?.trim()) {
      showError('Please enter your email address in the contact details step.');
      return;
    }
    if (userIsAdmin) {
      const testEmail = adminCartTestEmail?.trim();
      if (!testEmail) {
        showError('Please enter the test recipient email address.');
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(testEmail)) {
        showError('Please enter a valid test recipient email address.');
        return;
      }
    }
    if (!formData.shippingFirstName?.trim() || !formData.shippingLastName?.trim()) {
      showError('Please enter your name in the shipping details step.');
      return;
    }

    const token = localStorage.getItem('token');
    setIsSendingCartInquiry(true);
    try {
      const response = await fetch('/api/orders/test-cart-inquiry', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          items: mapCartItemsForApi(),
          subtotal,
          shipping: shipping.cost || 0,
          tax: tax.amount || 0,
          total,
          customerInfo: {
            email: formData.email,
            phone: formData.phone,
            shippingFirstName: formData.shippingFirstName,
            shippingLastName: formData.shippingLastName,
          },
          shippingAddress,
          personalMessage: cartInquiryMessage,
          ...(userIsAdmin ? { adminTestRecipient: adminCartTestEmail.trim() } : {}),
        }),
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || 'Failed to send cart');
      }

      setCartInquiryReference(result.referenceId || '');
      showSuccess(result.message || 'Your cart was sent to Paul Cranford.');
    } catch (error) {
      console.error('Cart inquiry error:', error);
      showError(error.message || 'Failed to send cart. Please try again.');
    } finally {
      setIsSendingCartInquiry(false);
    }
  };

  // Initialize payment providers
  useEffect(() => {
    let cancelled = false;

    const initPaymentProviders = async () => {
      const requestedStripeMode = adminStripeMode || getStoredAdminStripeMode();
      const requestedPayPalMode = adminPayPalMode || getStoredAdminPayPalMode();

      try {
        setSwitchingPaymentMode(true);
        console.log('💳 Fetching payment configuration...', { requestedStripeMode, requestedPayPalMode });
        const configResponse = await fetch('/api/payment/config', {
          cache: 'no-store',
          headers: paymentAuthHeaders(userIsAdmin, {
            stripeMode: requestedStripeMode,
            paypalMode: requestedPayPalMode,
          }),
        });
        console.log('💳 Config response status:', configResponse.status);

        if (cancelled) return;

        if (configResponse.ok) {
          const config = await configResponse.json();
          if (cancelled) return;
          console.log('💳 Payment config received:', {
            ...config,
            stripePublishableKey: config.stripePublishableKey
              ? `${String(config.stripePublishableKey).slice(0, 12)}…`
              : null,
            paypalClientId: config.paypalClientId
              ? `${String(config.paypalClientId).slice(0, 12)}…`
              : null,
          });
          setPaymentConfig(config);

          // Keep explicit admin picks; only sync from server when no override is stored
          const storedStripe = getStoredAdminStripeMode();
          const storedPaypal = getStoredAdminPayPalMode();
          setAdminStripeMode(storedStripe || config.stripeMode || config.defaultStripeMode);
          setAdminPayPalMode(storedPaypal || config.paypalMode || config.defaultPayPalMode);

          if (config.paymentModeSwitchError) {
            showError(config.paymentModeSwitchError);
          }

          const initErrors = [];
          setStripe(null);
          setPaypalReady(false);

          const keysHiddenForAdminOnly =
            Boolean(config.paymentsAdminOnly) &&
            !config.stripePublishableKey &&
            !config.paypalClientId;

          if (keysHiddenForAdminOnly) {
            // Expected for guests / non-admins when CHECKOUT_PAYMENTS_ADMIN_ONLY=true.
            // Do not treat this as a broken payment config — Email my cart still works.
            console.log('💳 Payment keys withheld (admin-only / sandbox checkout).', {
              userIsAdmin,
              adminSession: config.adminSession,
              hasToken: Boolean(localStorage.getItem('token')),
            });
            if (userIsAdmin && config.adminSession === false) {
              showError(
                'Your login session was not recognized for payments. Sign out and sign back in, then retry checkout.'
              );
            } else if (userIsAdmin) {
              // Admin UI says admin, but keys still missing — likely a stale cached config
              showError(
                'Could not load Stripe/PayPal keys. Hard-refresh the page (Cmd+Shift+R) or sign out and back in.'
              );
            }
          } else {
            if (config.stripePublishableKey?.startsWith('pk_')) {
              try {
                console.log('💳 Initializing Stripe...', config.stripeEnvironment || config.stripeMode);
                const StripeConstructor = await loadStripe();
                const stripeInstance = StripeConstructor(config.stripePublishableKey);
                setStripe(stripeInstance);
                console.log('✅ Stripe initialized');
              } catch (stripeError) {
                console.error('❌ Stripe initialization failed:', stripeError);
                initErrors.push('Stripe');
              }
            } else if (
              normalizeAvailableProviderModes(config)?.stripe?.[config.stripeMode || 'sandbox']
            ) {
              initErrors.push('Stripe (missing or invalid publishable key)');
            }

            if (config.paypalClientId) {
              try {
                console.log('💳 Initializing PayPal...');
                await loadPayPal(config.paypalClientId);
                if (cancelled) return;
                setPaypalReady(true);
                console.log('✅ PayPal initialized');
              } catch (paypalError) {
                console.error('❌ PayPal initialization failed:', paypalError);
                initErrors.push('PayPal');
              }
            } else if (
              normalizeAvailableProviderModes(config)?.paypal?.[config.paypalMode || 'sandbox']
            ) {
              initErrors.push('PayPal');
            }

            if (initErrors.length === 2) {
              throw new Error('Payment configuration is incomplete');
            }
            if (initErrors.length === 1) {
              showError(`${initErrors[0]} is unavailable. You can still use the other payment method.`);
            }
          }
        } else {
          const errBody = await configResponse.json().catch(() => ({}));
          throw new Error(errBody.error || `Failed to fetch config: ${configResponse.status}`);
        }
      } catch (error) {
        if (cancelled) return;
        console.error('❌ Error initializing payment providers:', error);
        showError('Failed to initialize payment providers: ' + error.message);
      } finally {
        if (!cancelled) {
          setSwitchingPaymentMode(false);
        }
      }
    };

    initPaymentProviders();
    return () => {
      cancelled = true;
      setPaypalReady(false);
    };
    // Re-fetch when auth or admin payment mode changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id, userIsAdmin, adminStripeMode, adminPayPalMode]);

  const handleAdminStripeModeChange = (mode) => {
    if (!userIsAdmin) return;
    if (mode !== 'live' && mode !== 'sandbox') return;
    const serverDefault = paymentConfig?.defaultStripeMode || paymentConfig?.defaultPaymentMode || 'sandbox';
    if (mode === serverDefault) {
      clearStoredAdminStripeMode();
    } else {
      setStoredAdminStripeMode(mode);
    }
    setAdminStripeMode(mode);
    setStripe(null);
  };

  const handleAdminPayPalModeChange = (mode) => {
    if (!userIsAdmin) return;
    if (mode !== 'live' && mode !== 'sandbox') return;
    const serverDefault = paymentConfig?.defaultPayPalMode || paymentConfig?.defaultPaymentMode || 'sandbox';
    if (mode === serverDefault) {
      clearStoredAdminPayPalMode();
    } else {
      setStoredAdminPayPalMode(mode);
    }
    setAdminPayPalMode(mode);
    setPaypalReady(false);
    const container = document.getElementById('paypal-button-container');
    if (container) container.innerHTML = '';
  };

  // Initialize PayPal buttons when ready and on payment step
  useEffect(() => {
    if (paypalReady && showPayPalPayment && currentStep === 2 && paymentMethod === 'paypal' && window.paypal) {
      const renderId = ++paypalButtonsRenderIdRef.current;
      // Small delay to ensure DOM is ready, especially on mobile
      const timer = setTimeout(() => {
        if (renderId === paypalButtonsRenderIdRef.current) {
          initializePayPalButtons(renderId);
        }
      }, 100);

      return () => {
        clearTimeout(timer);
        paypalButtonsRenderIdRef.current += 1;
        // Cleanup: clear container when component unmounts or dependencies change
        const container = document.getElementById('paypal-button-container');
        if (container) {
          container.innerHTML = '';
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paypalReady, showPayPalPayment, currentStep, paymentMethod, adminPayPalMode, paymentConfig?.paypalClientId]);

  // Pre-populate form with user profile data from backend
  useEffect(() => {
    let isMounted = true;
    const fetchUserProfile = async () => {
      if (!currentUser) {
        setSaveAddressToProfile(false);
        setProfileHasSavedAddress(false);
        setUserNewsletterSubscribed(false);
        setProfileLoaded(false);
        return;
      }

      setProfileLoaded(false);
      try {
          const token = localStorage.getItem('token');
          if (!token) return;

          const response = await fetch('/api/users/me', {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (response.ok && isMounted) {
            const profile = await response.json();
            console.log('📋 Loaded profile from API:', profile);
            const hasSavedAddress = hasProfileShippingAddress(profile);
            setProfileHasSavedAddress(hasSavedAddress);
            setSaveAddressToProfile(!hasSavedAddress);
            setUserNewsletterSubscribed(!!profile.newsletter_subscribed);
            setFormData(prev => ({
              ...prev,
              email: profile.email || prev.email,
              phone: profile.phone || prev.phone,
              shippingFirstName: profile.first_name || prev.shippingFirstName,
              shippingLastName: profile.last_name || prev.shippingLastName,
            }));

            // Populate shipping address from profile (do not spread stale default countryCode)
            setShippingAddress(() => {
              const next = profileToShippingAddress(profile);
              saveCustomerCountry(next);
              return next;
            });
          } else {
            console.error(`Failed to fetch user profile: ${response.status} - ${response.statusText}`);
            // Fallback to localStorage if API fails
            const savedProfile = localStorage.getItem(`userProfile_${currentUser.username}`);
            if (savedProfile) {
              try {
                const profile = JSON.parse(savedProfile);
                const hasSavedAddress = hasProfileShippingAddress(profile);
                setProfileHasSavedAddress(hasSavedAddress);
                setSaveAddressToProfile(!hasSavedAddress);
                setUserNewsletterSubscribed(!!profile.newsletter);
                setFormData(prev => ({
                  ...prev,
                  email: profile.email || prev.email,
                  phone: profile.phone || prev.phone,
                  shippingFirstName: profile.firstName || prev.shippingFirstName,
                  shippingLastName: profile.lastName || prev.shippingLastName,
                }));

                setShippingAddress(() => {
                  const next = profileToShippingAddress(profile);
                  saveCustomerCountry(next);
                  return next;
                });
              } catch (error) {
                console.error('Error loading profile data from localStorage:', error);
              }
            }
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
        } finally {
          if (isMounted) {
            setProfileLoaded(true);
          }
        }
    };

    fetchUserProfile();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.username]); // Use stable primitive value instead of object

  // Persist customer country for cart USD display
  useEffect(() => {
    if (shippingAddress.country || shippingAddress.countryCode) {
      saveCustomerCountry(shippingAddress);
    }
  }, [shippingAddress.country, shippingAddress.countryCode, shippingAddress.countryName]);

  // Auto-calculate shipping when address is populated or items change
  useEffect(() => {
    // Only calculate if we have minimum required address info
    if (shippingAddress.country && shippingAddress.city && shippingAddress.postalCode) {
      const countryCode = resolveCountryCode(shippingAddress);
      const stateProvince = getStateProvinceValue(shippingAddress, countryCode);

      if (countryCode) {
        updateShipping(shipping.method, countryCode, stateProvince);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shippingAddress.country, shippingAddress.countryCode, shippingAddress.province,
  shippingAddress.state, shippingAddress.region, shippingAddress.city,
  shippingAddress.postalCode, shipping.method, items]); // Added items dependency

  // Auto-calculate tax when address, shipping cost, or items change
  useEffect(() => {
    // Only calculate if we have minimum required address info
    if (shippingAddress.country && shippingAddress.city && shippingAddress.postalCode) {
      const countryCode = resolveCountryCode(shippingAddress);
      const stateProvince = getStateProvinceValue(shippingAddress, countryCode);

      if (countryCode) {
        updateTax(countryCode, stateProvince);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shippingAddress.country, shippingAddress.countryCode, shippingAddress.province,
  shippingAddress.state, shippingAddress.region, shippingAddress.city,
  shippingAddress.postalCode, shipping.cost, items]); // Added items dependency

  // Auto-calculate tariff when address or items change (for USA orders)
  useEffect(() => {
    // Only calculate if we have minimum required address info
    if (shippingAddress.country && shippingAddress.city && shippingAddress.postalCode) {
      const country = shippingAddress.country || shippingAddress.countryName || '';
      const countryCode = resolveCountryCode(shippingAddress);

      updateTariff(country, countryCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shippingAddress.country, shippingAddress.countryCode, shippingAddress.city,
  shippingAddress.postalCode, items]); // Recalculate when items change

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleShippingAddressChange = (newAddress) => {
    const normalizedAddress = normalizeAddress(newAddress);
    setShippingAddress(normalizedAddress);

    // Clear address-related errors when user changes address
    const addressErrorKeys = ['shippingStreet', 'shippingCity', 'shippingPostalCode',
      'shippingProvince', 'shippingState', 'shippingRegion', 'shippingCountryName'];
    setFieldErrors(prev => {
      const newErrors = { ...prev };
      addressErrorKeys.forEach(key => {
        if (newErrors[key]) {
          delete newErrors[key];
        }
      });
      return newErrors;
    });

    const countryCode = resolveCountryCode(normalizedAddress);
    const stateProvince = getStateProvinceValue(normalizedAddress, countryCode);

    if (countryCode) {
      updateShipping(shipping.method, countryCode, stateProvince);
      updateTax(countryCode, stateProvince);
    }
  };

  // Billing address always uses shipping address - no separate function needed

  // Handle Stripe payment
  const handleStripePayment = async () => {
    if (!stripe) {
      showError('Stripe is not initialized');
      return;
    }

    if (cartIsDigitalOnly && !currentUser) {
      showError('Digital downloads require a signed-in account. Please log in or register first.');
      return;
    }

    if (mustVerifyBeforePay) {
      showError('Please verify your email before checkout. Check your inbox or resend the link from your profile.');
      return;
    }

    setIsProcessing(true);
    try {
      const normalizedAddress = normalizeAddress(shippingAddress);
      const stateProvince = getStateProvinceValue(normalizedAddress);

      console.log('💳 Stripe Payment - Sending order data:');
      console.log('   Subtotal:', subtotal, 'cents');
      console.log('   Shipping:', shipping.cost || 0, 'cents');
      console.log('   Tax:', tax.amount || 0, 'cents');
      console.log('   Total:', total, 'cents');
      console.log('   Tax Breakdown:', tax.breakdown);

      const response = await fetch('/api/payment/create-stripe-session', {
        method: 'POST',
        headers: paymentAuthHeaders(userIsAdmin, checkoutPaymentModes(adminStripeMode, adminPayPalMode)),
        body: JSON.stringify({
          amount: Math.round(total), // total is already in cents (subtotal + shipping + tax)
          currency: 'cad',
          customerEmail: formData.email,
          customerName: `${formData.shippingFirstName} ${formData.shippingLastName}`,
          orderData: {
            items: items.map(item => ({
              id: item.id,
              name: item.name,
              quantity: item.quantity,
              unitPrice: getLineUnitPriceCents(item),
              // Include bundle-related fields if this is a bundle
              isBundle: item.isBundle || false,
              bundleProducts: item.bundleProducts || [],
              bundleOptionName: item.bundleOptionName || null,
              bundleTaxAllocations: item.bundleTaxAllocations || null,
              bundleDiscount: item.bundleDiscount || 0,
              originalPrice: item.originalPrice || 0,
              // Include other product fields that might be needed
              price_cents: getLineUnitPriceCents(item),
              price: getLineUnitPriceCents(item),
              selectedSize: item.selectedSize || null,
              selectedColor: item.selectedColor || null
            })),
            customerInfo: {
              email: formData.email,
              phone: formData.phone,
              userId: currentUser?.id || null,
              shippingFirstName: formData.shippingFirstName,
              shippingLastName: formData.shippingLastName,
              shippingAddress1: normalizedAddress.street || '',
              shippingAddress2: normalizedAddress.street2 || '',
              shippingCity: normalizedAddress.city || '',
              shippingStateProvince: stateProvince,
              shippingPostalCode: normalizedAddress.postalCode || '',
              shippingCountry: normalizedAddress.country || normalizedAddress.countryName || '',
              shippingCountryCode: resolveCountryCode(normalizedAddress) === 'INTL'
                ? ''
                : resolveCountryCode(normalizedAddress),
            },
            subtotal,
            shipping: shipping.cost || 0,
            tax: tax.amount || 0,
            taxBreakdown: tax.breakdown || [],
            transactionFee,
            transactionFeePct: transactionFeeInfo.pct || 0,
            total,
            shippingDetails: buildShippingDetailsPayload(
              resolveCountryCode(normalizedAddress) === 'INTL'
                ? (shipping.country || '')
                : (resolveCountryCode(normalizedAddress) || shipping.country || '')
            ),
          }
        })
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || 'Failed to create Stripe checkout session');
      }

      const { sessionId } = result;
      if (cartHasDigital) {
        sessionStorage.setItem('checkoutHadDigital', '1');
      } else {
        sessionStorage.removeItem('checkoutHadDigital');
      }
      if (cartHasPhysical) {
        sessionStorage.setItem('checkoutHadPhysical', '1');
      } else {
        sessionStorage.removeItem('checkoutHadPhysical');
      }
      const { error } = await stripe.redirectToCheckout({ sessionId });

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Stripe payment error:', error);
      showError(error.message || 'Payment failed');
      setIsProcessing(false);
    }
  };

  // Initialize PayPal buttons
  const initializePayPalButtons = (renderId = paypalButtonsRenderIdRef.current) => {
    const container = document.getElementById('paypal-button-container');
    if (!container) {
      console.error('PayPal button container not found');
      return;
    }
    if (renderId !== paypalButtonsRenderIdRef.current) {
      return;
    }

    // Clear container before rendering (important for mobile)
    container.innerHTML = '';

    try {
      window.paypal.Buttons({
        fundingSource: window.paypal.FUNDING.PAYPAL,
        style: {
          layout: 'vertical',
          color: 'gold',
          shape: 'rect',
          label: 'pay',
          height: window.innerWidth <= 768 ? 50 : 48
        },
        createOrder: async (data, actions) => {
          try {
            if (mustVerifyBeforePay) {
              throw new Error('Please verify your email before checkout. Check your inbox or resend the link from your profile.');
            }
            // Validate required fields
            if (!formData.email?.trim()) {
              throw new Error('Email address is required');
            }
            if (!formData.shippingFirstName?.trim() || !formData.shippingLastName?.trim()) {
              throw new Error('Shipping name is required');
            }
            if (!shippingAddress.street?.trim()) {
              throw new Error('Shipping address is required');
            }
            if (!shippingAddress.city?.trim()) {
              throw new Error('City is required');
            }
            if (!shippingAddress.postalCode?.trim()) {
              throw new Error('Postal code is required');
            }

            const normalizedAddress = normalizeAddress(shippingAddress);
            const countryCode = resolveCountryCode(normalizedAddress);
            if (countryCode === 'CA' && !normalizedAddress.province?.trim()) {
              throw new Error('Province is required for Canadian addresses');
            }
            if (countryCode === 'US' && !normalizedAddress.state?.trim()) {
              throw new Error('State is required for US addresses');
            }

            const stateProvince = getStateProvinceValue(normalizedAddress, countryCode);

            console.log('Creating PayPal order with data:', {
              amount: Math.round(total),
              email: formData.email,
              shippingAddress: {
                street: shippingAddress.street,
                city: shippingAddress.city,
                postalCode: shippingAddress.postalCode,
                country: shippingAddress.country
              }
            });

            // Attempt server-side order creation with timeout
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 8000);
            const response = await fetch('/api/payment/create-paypal-order', {
              method: 'POST',
              headers: paymentAuthHeaders(userIsAdmin, checkoutPaymentModes(adminStripeMode, adminPayPalMode)),
              body: JSON.stringify({
                amount: Math.round(total), // total is already in cents
                currency: 'CAD',
                orderData: {
                  items: items.map(item => ({
                    id: item.id,
                    name: item.name,
                    quantity: item.quantity,
                    unitPrice: getLineUnitPriceCents(item),
                    // Include bundle-related fields if this is a bundle
                    isBundle: item.isBundle || false,
                    bundleProducts: item.bundleProducts || [],
                    bundleOptionName: item.bundleOptionName || null,
                    bundleTaxAllocations: item.bundleTaxAllocations || null,
                    bundleDiscount: item.bundleDiscount || 0,
                    originalPrice: item.originalPrice || 0,
                    // Include other product fields that might be needed
                    price_cents: getLineUnitPriceCents(item),
                    price: getLineUnitPriceCents(item),
                    selectedSize: item.selectedSize || null,
                    selectedColor: item.selectedColor || null
                  })),
                  customerInfo: {
                    email: formData.email,
                    phone: formData.phone,
                    userId: currentUser?.id || null,
                    shippingFirstName: formData.shippingFirstName,
                    shippingLastName: formData.shippingLastName,
                    shippingAddress1: normalizedAddress.street || '',
                    shippingAddress2: normalizedAddress.street2 || '',
                    shippingCity: normalizedAddress.city || '',
                    shippingStateProvince: stateProvince,
                    shippingPostalCode: normalizedAddress.postalCode || '',
                    shippingCountry: normalizedAddress.country || normalizedAddress.countryName || ''
                  },
                  subtotal,
                  shipping: shipping.cost || 0,
                  tax: tax.amount || 0,
                  transactionFee,
                  transactionFeePct: transactionFeeInfo.pct || 0,
                  total,
                  shippingDetails: buildShippingDetailsPayload(),
                }
              }),
              signal: controller.signal
            });
            clearTimeout(timeoutId);

            if (!response.ok) {
              const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
              throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
            }

            const dataResp = await response.json();

            if (!dataResp.orderID) {
              throw new Error(dataResp.error || 'No order ID returned from server');
            }

            return dataResp.orderID;
          } catch (error) {
            console.error('PayPal order creation error (fallback to client create):', error);
            // Fallback: create client-side order to avoid spinner hang
            try {
              let amountValue;
              let subtotalValue;
              let shippingValue;
              let taxValue;

              if (typeof total === 'number' && !Number.isNaN(total)) {
                amountValue = total >= 100 ? (total / 100).toFixed(2) : total.toFixed(2);
                subtotalValue = subtotal >= 100 ? (subtotal / 100).toFixed(2) : subtotal.toFixed(2);
                shippingValue = (shipping?.cost >= 100 ? (shipping.cost / 100).toFixed(2) : (shipping?.cost || 0).toFixed(2));
                taxValue = (tax?.amount >= 100 ? (tax.amount / 100).toFixed(2) : (tax?.amount || 0).toFixed(2));
              } else {
                const itemsTotalCents = items.reduce((sum, item) => {
                  const priceCents = getLineUnitPriceCents(item);
                  return sum + priceCents * (item.quantity || 1);
                }, 0);
                const shippingCents = Math.round((typeof shipping?.cost === 'number' ? shipping.cost * 100 : (shipping?.costCents || 0)));
                const taxCents = Math.round((typeof tax?.amount === 'number' ? tax.amount * 100 : (tax?.amountCents || 0)));
                amountValue = ((itemsTotalCents + shippingCents + taxCents) / 100).toFixed(2);
                subtotalValue = (itemsTotalCents / 100).toFixed(2);
                shippingValue = (shippingCents / 100).toFixed(2);
                taxValue = (taxCents / 100).toFixed(2);
              }

              return actions.order.create({
                purchase_units: [
                  {
                    amount: {
                      currency_code: 'CAD',
                      value: amountValue,
                      breakdown: {
                        item_total: {
                          currency_code: 'CAD',
                          value: subtotalValue
                        },
                        shipping: {
                          currency_code: 'CAD',
                          value: shippingValue
                        },
                        tax_total: {
                          currency_code: 'CAD',
                          value: taxValue
                        }
                      }
                    }
                  }
                ]
              });
            } catch (fallbackError) {
              console.error('Client-side PayPal order create failed:', fallbackError);
              showError(fallbackError.message || 'Failed to create PayPal order. Please try again.');
              throw fallbackError;
            }
          }
        },
        onApprove: async (data) => {
          setIsProcessing(true);
          try {
            const response = await fetch('/api/payment/capture-paypal-payment', {
              method: 'POST',
              headers: paymentAuthHeaders(userIsAdmin, checkoutPaymentModes(adminStripeMode, adminPayPalMode)),
              body: JSON.stringify({ orderID: data.orderID })
            });
            const result = await response.json();

            if (result.success) {
              setCompletedOrderDetails({
                items: [...items],
                products: [...products],
                subtotal,
                shipping: shipping.cost || 0,
                tax: tax.amount || 0,
                total,
                paymentMode: result.paypalMode || result.paymentMode || selectedPayPalMode,
                provider: 'paypal',
                hasDigital: cartHasDigital,
                hasPhysical: cartHasPhysical,
              });
              setOrderNumber(result.orderNumber);
              setOrderComplete(true);
              showSuccess(`Order ${result.orderNumber} placed successfully!`);
              clearCart();
            } else {
              throw new Error(result.error || 'Payment capture failed');
            }
          } catch (error) {
            console.error('PayPal capture error:', error);
            showError(error.message || 'Payment capture failed');
          } finally {
            setIsProcessing(false);
          }
        },
        onError: (err) => {
          console.error('PayPal error:', err);
          const errorMessage = err?.message || err?.toString() || 'PayPal payment failed';
          showError(`PayPal error: ${errorMessage}. Please try again or use a different payment method.`);
          setIsProcessing(false);
        },
        onCancel: (data) => {
          console.log('PayPal payment cancelled:', data);
          setIsProcessing(false);
        }
      })
        .render('#paypal-button-container')
        .catch((error) => {
          if (renderId !== paypalButtonsRenderIdRef.current) return;
          console.error('PayPal button render failed:', error);
          showError('Failed to load PayPal button. Refresh the page or switch PayPal sandbox/live mode.');
        });
    } catch (error) {
      console.error('Error initializing PayPal buttons:', error);
      showError('Failed to initialize PayPal button. Please refresh the page.');
    }
  };

  const validateShippingInfo = () => {
    const errors = {};
    const normalizedAddress = normalizeAddress(shippingAddress);
    const countryCode = resolveCountryCode(normalizedAddress);

    // Contact Information
    if (!formData.email?.trim()) {
      errors.email = c.errEmailRequired;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = c.errEmailInvalid;
    }

    if (!formData.phone?.trim()) {
      errors.phone = c.errPhoneRequired;
    } else if (!/^[\d\s\-+()]{10,}$/.test(formData.phone)) {
      errors.phone = c.errPhoneInvalid;
    }

    // Shipping Name
    if (!formData.shippingFirstName?.trim()) {
      errors.shippingFirstName = c.errFirstName;
    }

    if (!formData.shippingLastName?.trim()) {
      errors.shippingLastName = c.errLastName;
    }

    // Shipping Address
    if (!normalizedAddress.street?.trim()) {
      errors.shippingStreet = c.errStreet;
    }

    if (!normalizedAddress.city?.trim()) {
      errors.shippingCity = c.errCity;
    }

    if (!normalizedAddress.postalCode?.trim()) {
      errors.shippingPostalCode = c.errPostal;
    }

    // Validate province/state/region based on resolved country
    if (countryCode === 'CA' && !normalizedAddress.province?.trim()) {
      errors.shippingProvince = c.errProvince;
    }

    if (countryCode === 'US' && !normalizedAddress.state?.trim()) {
      errors.shippingState = c.errState;
    }

    if (countryCode === 'INTL') {
      if (!normalizedAddress.region?.trim()) {
        errors.shippingRegion = c.errRegion;
      }
      if (!normalizedAddress.countryName?.trim()) {
        errors.shippingCountryName = c.errCountryName;
      }
    }

    return errors;
  };

  const validatePaymentInfo = () => {
    const errors = {};

    // Billing address validation removed - always uses shipping address

    // Validate payment card
    if (!formData.cardNumber?.trim()) {
      errors.cardNumber = 'Card number is required';
    } else if (!/^\d{13,19}$/.test(formData.cardNumber.replace(/\s/g, ''))) {
      errors.cardNumber = 'Please enter a valid card number';
    }

    if (!formData.expiryDate?.trim()) {
      errors.expiryDate = 'Expiry date is required';
    } else if (!/^(0[1-9]|1[0-2])\/\d{2}$/.test(formData.expiryDate)) {
      errors.expiryDate = 'Please enter date as MM/YY';
    }

    if (!formData.cvv?.trim()) {
      errors.cvv = 'CVV is required';
    } else if (!/^\d{3,4}$/.test(formData.cvv)) {
      errors.cvv = 'CVV must be 3 or 4 digits';
    }

    return errors;
  };

  const saveShippingAddressToProfile = async () => {
    const token = localStorage.getItem('token');
    if (!token || !currentUser) return false;

    const normalizedAddress = normalizeAddress(shippingAddress);
    const countryCode = resolveCountryCode(normalizedAddress);
    const stateProvince = getStateProvinceValue(normalizedAddress, countryCode);

    try {
      const response = await fetch('/api/users/me', {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          first_name: formData.shippingFirstName,
          last_name: formData.shippingLastName,
          phone: formData.phone,
          address_line1: normalizedAddress.street,
          address_line2: normalizedAddress.street2 || '',
          city: normalizedAddress.city,
          state_province: stateProvince,
          postal_code: normalizedAddress.postalCode,
          country: normalizedAddress.country,
          newsletter_subscribed: userNewsletterSubscribed,
        }),
      });

      if (!response.ok) {
        console.error('Failed to save shipping address to profile:', response.status);
        return false;
      }

      const combinedData = {
        firstName: formData.shippingFirstName,
        lastName: formData.shippingLastName,
        phone: formData.phone,
        addressLine1: normalizedAddress.street,
        addressLine2: normalizedAddress.street2 || '',
        street: normalizedAddress.street,
        street2: normalizedAddress.street2 || '',
        city: normalizedAddress.city,
        province: stateProvince,
        state: normalizedAddress.state,
        region: normalizedAddress.region,
        postalCode: normalizedAddress.postalCode,
        country: normalizedAddress.country,
        countryCode: normalizedAddress.countryCode,
        countryName: normalizedAddress.countryName,
        newsletter: userNewsletterSubscribed,
      };
      localStorage.setItem(`userProfile_${currentUser.username}`, JSON.stringify(combinedData));
      return true;
    } catch (error) {
      console.error('Error saving shipping address to profile:', error);
      return false;
    }
  };

  const handleNextStep = async () => {
    const errors = validateShippingInfo();

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      const normalizedAddress = normalizeAddress(shippingAddress);
      if (resolveCountryCode(normalizedAddress) !== normalizedAddress.countryCode) {
        setShippingAddress(normalizedAddress);
      }

      const errorCount = Object.keys(errors).length;
      const fieldNames = {
        email: 'Email',
        phone: 'Phone',
        shippingFirstName: 'First name',
        shippingLastName: 'Last name',
        shippingStreet: 'Street address',
        shippingCity: 'City',
        shippingPostalCode: 'Postal code',
        shippingProvince: 'Province',
        shippingState: 'State',
        shippingRegion: 'Region',
        shippingCountryName: 'Country',
      };
      const errorFields = Object.keys(errors).map(key => fieldNames[key] || key).join(', ');

      showError(`${errorCount} field${errorCount > 1 ? 's' : ''} need${errorCount === 1 ? 's' : ''} attention: ${errorFields}. Please check the highlighted fields below.`);

      // Scroll to first error field
      setTimeout(() => {
        const firstErrorField = document.querySelector('.form-group input.error, .form-group select.error');
        if (firstErrorField) {
          firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
          firstErrorField.focus();
        }
      }, 100);

      return;
    }

    setShippingAddress(prev => normalizeAddress(prev));

    if (currentUser && saveAddressToProfile) {
      setIsSavingProfileAddress(true);
      const saved = await saveShippingAddressToProfile();
      setIsSavingProfileAddress(false);
      if (saved) {
        setProfileHasSavedAddress(true);
        showSuccess('Shipping saved — address added to your profile');
      } else {
        showError('Could not save address to your profile. You can update it later under Profile.');
      }
    } else {
      showSuccess('Shipping information saved');
    }

    if (cartIsDigitalOnly && !currentUser) {
      showError('Digital downloads require a signed-in account. Please log in or register before payment.');
      return;
    }

    if (mustVerifyBeforePay) {
      showError('Please verify your email before checkout. Check your inbox or resend the link from your profile.');
      return;
    }

    setCurrentStep(2);
  };

  const showShippingValidationErrors = (errors) => {
    setFieldErrors(errors);
    const normalizedAddress = normalizeAddress(shippingAddress);
    if (resolveCountryCode(normalizedAddress) !== normalizedAddress.countryCode) {
      setShippingAddress(normalizedAddress);
    }

    const errorCount = Object.keys(errors).length;
    const fieldNames = {
      email: 'Email',
      phone: 'Phone',
      shippingFirstName: 'First name',
      shippingLastName: 'Last name',
      shippingStreet: 'Street address',
      shippingCity: 'City',
      shippingPostalCode: 'Postal code',
      shippingProvince: 'Province',
      shippingState: 'State',
      shippingRegion: 'Region',
      shippingCountryName: 'Country',
    };
    const errorFields = Object.keys(errors).map((key) => fieldNames[key] || key).join(', ');
    showError(`${errorCount} field${errorCount > 1 ? 's' : ''} need${errorCount === 1 ? 's' : ''} attention: ${errorFields}. Please check the highlighted fields below.`);

    setTimeout(() => {
      const firstErrorField = document.querySelector('.form-group input.error, .form-group select.error');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        firstErrorField.focus();
      }
    }, 100);
  };

  const handleCreateMailOrderFromShipping = () => {
    if (!items.length) {
      showError('Your cart is empty.');
      return;
    }

    const errors = validateShippingInfo();
    if (Object.keys(errors).length > 0) {
      showShippingValidationErrors(errors);
      return;
    }

    setShippingAddress((prev) => normalizeAddress(prev));
    setMailOrderDraft(buildMailOrderDraftFromCheckout({
      items,
      formData,
      shippingAddress: normalizeAddress(shippingAddress),
      shipping,
      tax,
    }));
    setShowMailOrderModal(true);
  };

  const handleMailOrderCreatedFromCheckout = async (order) => {
    showSuccess(`Mail order ${order.order_number} created.`);
    clearCart();
    setShowMailOrderModal(false);
    setMailOrderDraft(null);
    navigate('/admin/orders');
  };

  const handleEmailMailOrderInvoiceFromCheckout = async (order, options = {}) => {
    await sendMailOrderInvoiceEmail(order, options);
  };

  const handlePreviousStep = () => {
    setCurrentStep(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate payment info
    const paymentErrors = validatePaymentInfo();

    if (Object.keys(paymentErrors).length > 0) {
      setFieldErrors(paymentErrors);
      showError('Please check your payment information');
      return;
    }

    setIsProcessing(true);

    console.log('💳 Checkout: Processing order...');
    console.log('🛒 Checkout: Items in cart:', items);
    console.log('👤 Checkout: Customer data:', formData);

    try {
      // Determine the state/province value based on country
      const stateProvince = shippingAddress.countryCode === 'CA' ? shippingAddress.province :
        shippingAddress.countryCode === 'US' ? shippingAddress.state :
          shippingAddress.region;

      // Prepare order data
      const orderData = {
        customerInfo: {
          email: formData.email,
          phone: formData.phone,
          shippingFirstName: formData.shippingFirstName,
          shippingLastName: formData.shippingLastName,
          shippingAddress1: shippingAddress.street,
          shippingAddress2: shippingAddress.street2,
          shippingCity: shippingAddress.city,
          shippingStateProvince: stateProvince,
          shippingPostalCode: shippingAddress.postalCode,
          shippingCountry: shippingAddress.country
        },
        items: items.map(item => ({
          id: item.id,
          name: item.name,
          quantity: item.quantity,
          unitPrice: getLineUnitPriceCents(item),
          // Include bundle-related fields if this is a bundle
          isBundle: item.isBundle || false,
          bundleProducts: item.bundleProducts || [],
          bundleOptionName: item.bundleOptionName || null,
          bundleTaxAllocations: item.bundleTaxAllocations || null,
          bundleDiscount: item.bundleDiscount || 0,
          originalPrice: item.originalPrice || 0,
          // Include other product fields that might be needed
          price_cents: getLineUnitPriceCents(item),
          price: getLineUnitPriceCents(item),
          selectedSize: item.selectedSize || null,
          selectedColor: item.selectedColor || null
        })),
        subtotal: subtotal,
        shipping: shipping.cost || 0,
        tax: tax.amount || 0,
        transactionFee,
        transactionFeePct: transactionFeeInfo.pct || 0,
        total: total,
        shippingDetails: buildShippingDetailsPayload(),
      };

      // Call orders API
      const response = await fetch('/api/orders/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(orderData)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        // Save order details BEFORE clearing cart
        setCompletedOrderDetails({
          items: [...items],
          products: [...products],
          subtotal: subtotal,
          shipping: shipping.cost || 0,
          tax: tax.amount || 0,
          total: total,
          hasDigital: cartHasDigital,
          hasPhysical: cartHasPhysical,
        });

        setOrderNumber(result.orderNumber);
        setOrderComplete(true);

        console.log('✅ Checkout: Order completed with number:', result.orderNumber);
        console.log('📧 Checkout: Confirmation emails sent:', result.emailsSent);
        console.log('📦 Checkout: Inventory has been decremented automatically by cart system');

        showSuccess(`Order ${result.orderNumber} placed successfully!`);

        // Clear the cart (inventory was already decremented when items were added)
        clearCart();
      } else {
        throw new Error(result.error || 'Failed to create order');
      }
    } catch (error) {
      console.error('❌ Checkout: Error processing order:', error);
      showError('Failed to process order. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  if (orderComplete && completedOrderDetails) {
    const completedHasDigital = Boolean(completedOrderDetails.hasDigital);
    const completedHasPhysical = Boolean(completedOrderDetails.hasPhysical);
    const completedIsMixed = completedHasDigital && completedHasPhysical;

    return (
      <div className="checkout-container success-page">
        <div className="checkout-content">
          {(() => {
            const completedMode =
              completedOrderDetails?.paymentMode ||
              (completedOrderDetails?.provider === 'paypal'
                ? selectedPayPalMode
                : completedOrderDetails?.provider === 'stripe'
                  ? selectedStripeMode
                  : null);
            const completedIsSandbox = completedMode === 'sandbox';
            const completedIsLive = completedMode === 'live';
            if (completedIsSandbox) {
              return (
                <div className="test-notice-banner test-notice-banner--sandbox">
                  <p style={{ margin: 0 }}>
                    <strong>🧪 Sandbox test order</strong> — This checkout used{' '}
                    {completedOrderDetails?.provider === 'paypal' ? 'PayPal' : 'Stripe'}{' '}
                    <strong>test (sandbox) accounts</strong>. No real charge was made.
                  </p>
                </div>
              );
            }
            if (completedIsLive) {
              return (
                <div
                  className="test-notice-banner"
                  style={{
                    background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                    border: '1px solid #86efac',
                    color: '#14532d',
                  }}
                >
                  <p style={{ margin: 0 }}>
                    <strong>💳 Live payment</strong> — This checkout used{' '}
                    {completedOrderDetails?.provider === 'paypal' ? 'PayPal' : 'Stripe'}{' '}
                    <strong>live</strong> accounts. A real charge was processed.
                  </p>
                </div>
              );
            }
            return null;
          })()}

          <div className="order-confirmation-card success">
            <div className="confirmation-header">
              <h1>🎉 {t.shop.success.confirmed}</h1>
              <p className="confirmation-subtitle">{t.shop.success.thankYou}</p>
            </div>

            <div className="order-details-box">
              <div className="order-number-badge">
                <span className="badge-label">{t.shop.success.orderNumber}</span>
                <span className="badge-number">{orderNumber}</span>
              </div>
            </div>

            <div className="success-message-box">
              <div className="message-icon">📧</div>
              <div className="message-content">
                <h3>{t.shop.success.emailSent}</h3>
                <p>{t.shop.success.emailSentText}</p>
              </div>
            </div>

            <div className="what-happens-next">
              <h3>
                {completedHasDigital ? '📥' : '📦'} {t.shop.success.next}
              </h3>
              <div className="timeline">
                <div className="timeline-item completed">
                  <div className="timeline-icon">✓</div>
                  <div className="timeline-content">
                    <h4>{t.shop.success.received}</h4>
                    <p>{t.shop.success.receivedText}</p>
                  </div>
                </div>
                {completedHasDigital ? (
                  <>
                    <div className="timeline-item">
                      <div className="timeline-icon">⏳</div>
                      <div className="timeline-content">
                        <h4>{t.shop.success.review}</h4>
                        <p>{t.shop.success.reviewText}</p>
                      </div>
                    </div>
                    <div className="timeline-item">
                      <div className="timeline-icon">📥</div>
                      <div className="timeline-content">
                        <h4>{t.shop.success.download}</h4>
                        <p>{t.shop.success.downloadText}</p>
                      </div>
                    </div>
                    {!completedIsMixed && (
                      <div className="timeline-item">
                        <div className="timeline-icon">🔁</div>
                        <div className="timeline-content">
                          <h4>{t.shop.success.redownload}</h4>
                          <p>{t.shop.success.redownloadText}</p>
                        </div>
                      </div>
                    )}
                  </>
                ) : null}
                {completedHasPhysical ? (
                  <>
                    <div className="timeline-item">
                      <div className="timeline-icon">📋</div>
                      <div className="timeline-content">
                        <h4>{t.shop.success.processing}</h4>
                        <p>{t.shop.success.processingText}</p>
                      </div>
                    </div>
                    <div className="timeline-item">
                      <div className="timeline-icon">📦</div>
                      <div className="timeline-content">
                        <h4>{t.shop.success.shipping}</h4>
                        <p>{t.shop.success.shippingText}</p>
                      </div>
                    </div>
                    <div className="timeline-item">
                      <div className="timeline-icon">🏠</div>
                      <div className="timeline-content">
                        <h4>{t.shop.success.delivery}</h4>
                        <p>{t.shop.success.deliveryText}</p>
                      </div>
                    </div>
                  </>
                ) : null}
              </div>
            </div>

            <div className="action-buttons">
              <button
                className="primary-action-btn"
                onClick={() => window.location.href = '/shop'}
              >
                {getIcon('shop')} {cartUi.continueShopping}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="checkout-container">
        <div className="empty-cart">
          <h2>{cartUi.emptyCheckoutTitle}</h2>
          <p>{cartUi.emptyCheckoutText}</p>
          <button
            className="continue-shopping-btn"
            onClick={() => window.location.href = '/shop'}
          >
            {getIcon('shop')} {cartUi.continueShopping}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-container">
      <div className="checkout-content">
        <h2>{c.title}</h2>

        {/* Progress Indicator / Roadmap */}
        <div className="checkout-progress">
          <button
            onClick={(e) => {
              e.preventDefault();
              window.location.href = '/shop';
            }}
            className={`progress-step shopping-step completed`}
            title={c.returnToShop}
            type="button"
          >
            <div className="step-number">{getIcon('shop')}</div>
            <div className="step-label">{c.stepShopping}</div>
          </button>
          <div className="progress-line"></div>
          <div className={`progress-step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
            <div className="step-number">1</div>
            <div className="step-label">{c.stepShipping}</div>
          </div>
          <div className="progress-line"></div>
          <div className={`progress-step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
            <div className="step-number">2</div>
            <div className="step-label">{c.stepPayment}</div>
          </div>
          <div className="progress-line"></div>
          <div className={`progress-step ${currentStep >= 3 ? 'active' : ''}`}>
            <div className="step-number">3</div>
            <div className="step-label">{c.stepConfirmation}</div>
          </div>
        </div>

        <div className="checkout-sections">
          <div className="checkout-form">
            {/* Guest/Login Options */}
            {!currentUser && (
              <div className="checkout-auth-section">
                <div className="checkout-auth-options">
                  <Link
                    to={`/login?returnUrl=${CHECKOUT_RETURN_ENC}`}
                    className="checkout-login-btn"
                  >
                    {c.signIn}
                  </Link>
                  <Link
                    to={`/register?returnUrl=${CHECKOUT_RETURN_ENC}`}
                    className="checkout-register-link"
                  >
                    {c.signUp}
                  </Link>
                  <span className="guest-indicator">{c.continueGuest}</span>
                </div>
              </div>
            )}

            {currentUser && (
              <div className="checkout-user-info">
                <h3>👤 {fmt(c.loggedInAs, { name: currentUser.username })}</h3>
                <p>{c.savedForFuture}</p>
                {mustVerifyBeforePay && (
                  <div
                    className="test-notice-banner"
                    style={{ marginTop: '0.75rem', borderColor: '#c9a227', background: 'rgba(201, 162, 39, 0.12)' }}
                  >
                    <strong>{c.verifyTitle}</strong>
                    <p style={{ margin: '0.35rem 0 0' }}>
                      {c.verifyBody}{' '}
                      <Link to="/profile">{c.resendFromProfile}</Link>.
                    </p>
                  </div>
                )}
              </div>
            )}

            <form onSubmit={handleSubmit} autoComplete="on">
              {currentStep === 1 && (
                <div className="form-section">
                  {cartHasDigital && (
                    <div
                      className="test-notice-banner"
                      style={{
                        marginBottom: '0.75rem',
                        background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                        border: '1px solid #93c5fd',
                        color: '#1e3a8a',
                      }}
                    >
                      {cartIsMixedDigitalPhysical ? (
                        <>📥 {c.digitalMixed}</>
                      ) : (
                        <>📥 {c.digitalOnly}</>
                      )}
                    </div>
                  )}
                  <div className="checkout-contact-fields">
                    <div className="form-group">
                      <label htmlFor="checkout-email">{c.email} <span className="required">*</span></label>
                      <input
                        type="email"
                        id="checkout-email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`checkout-field-input ${fieldErrors.email ? 'error' : ''}`}
                        required
                        autoComplete="email"
                        placeholder={c.emailPlaceholder}
                      />
                      <span className="field-hint">{c.emailHint}</span>
                      {fieldErrors.email && <span className="field-error">{fieldErrors.email}</span>}
                    </div>
                    <div className="form-group">
                      <label htmlFor="checkout-phone">{c.phone} <span className="required">*</span></label>
                      <input
                        type="text"
                        inputMode="tel"
                        id="checkout-phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className={`checkout-field-input ${fieldErrors.phone ? 'error' : ''}`}
                        required
                        autoComplete="tel"
                        placeholder="(123) 456-7890"
                      />
                      <span className="field-hint">{c.phoneHint}</span>
                      {fieldErrors.phone && <span className="field-error">{fieldErrors.phone}</span>}
                    </div>
                  </div>

                  <h3>📦 {c.shippingAddress}</h3>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="checkout-shipping-first-name">{c.firstName} <span className="required">*</span></label>
                      <input
                        type="text"
                        id="checkout-shipping-first-name"
                        name="shippingFirstName"
                        value={formData.shippingFirstName}
                        onChange={handleInputChange}
                        className={`checkout-field-input ${fieldErrors.shippingFirstName ? 'error' : ''}`}
                        required
                        autoComplete="shipping given-name"
                      />
                      {fieldErrors.shippingFirstName && <span className="field-error">{fieldErrors.shippingFirstName}</span>}
                    </div>
                    <div className="form-group">
                      <label htmlFor="checkout-shipping-last-name">{c.lastName} <span className="required">*</span></label>
                      <input
                        type="text"
                        id="checkout-shipping-last-name"
                        name="shippingLastName"
                        value={formData.shippingLastName}
                        onChange={handleInputChange}
                        className={`checkout-field-input ${fieldErrors.shippingLastName ? 'error' : ''}`}
                        required
                        autoComplete="shipping family-name"
                      />
                      {fieldErrors.shippingLastName && <span className="field-error">{fieldErrors.shippingLastName}</span>}
                    </div>
                  </div>

                  {/* Dynamic Address Form */}
                  <AddressForm
                    address={shippingAddress}
                    onChange={handleShippingAddressChange}
                    disabled={false}
                    prefix="shipping"
                    errors={fieldErrors}
                  />

                  {currentUser && profileLoaded && (
                    <div className="checkbox-group save-profile-address-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={saveAddressToProfile}
                          onChange={(e) => setSaveAddressToProfile(e.target.checked)}
                        />
                        <span>
                          {profileHasSavedAddress
                            ? c.updateProfileAddress
                            : c.saveProfileAddress}
                        </span>
                      </label>
                      <p className="save-profile-address-hint">
                        {profileHasSavedAddress
                          ? c.updateProfileHint
                          : c.saveProfileHint}
                      </p>
                    </div>
                  )}

                  <div className="checkout-shipping-actions">
                    <button
                      type="button"
                      onClick={handleNextStep}
                      className="next-step-btn"
                      disabled={isSavingProfileAddress || mustVerifyBeforePay}
                      title={
                        mustVerifyBeforePay
                          ? c.verifyBeforePay
                          : undefined
                      }
                    >
                      {isSavingProfileAddress
                        ? c.savingProfile
                        : mustVerifyBeforePay
                          ? c.verifyToContinue
                          : `${getIcon('forward')} ${c.continuePayment}`}
                    </button>
                    {showMailOrderCheckout && (
                      <button
                        type="button"
                        onClick={handleCreateMailOrderFromShipping}
                        className="mail-order-shipping-btn"
                        disabled={!items.length}
                      >
                        {MAIL_ORDER_ACTION_LABEL}
                      </button>
                    )}
                  </div>
                  {showMailOrderCheckout && (
                    <p className="mail-order-shipping-hint">
                      {c.mailOrderHint}
                    </p>
                  )}
                </div>
              )}

              {currentStep === 2 && (
                <div className="form-section">
                  {showEmailCart && !showStripePayment && !showPayPalPayment && (
                    <div className="test-notice-banner">
                      {c.emailCartUnavailable}
                    </div>
                  )}

                  <h3>💳 {c.payment}</h3>
                  <p className="payment-step-intro">
                    {showEmailCart && !showStripePayment && !showPayPalPayment
                      ? c.paymentIntroEmail
                      : c.paymentIntroChoose}
                  </p>

                  {!hasAnyPaymentMethod && (
                    <div className="payment-unavailable-notice">
                      <p>
                        {c.paymentUnavailable}
                      </p>
                    </div>
                  )}

                  {/* Payment Method Selection — step 1 */}
                  {hasAnyPaymentMethod && (
                  <div className="payment-method-step">
                    <p className="payment-step-label">{c.step1Method}</p>

                    <div
                      className={`payment-method-selection${showEmailCart ? ' payment-method-selection--with-email' : ''}`}
                      role="radiogroup"
                      aria-label={c.paymentMethodAria}
                    >
                      {showStripePayment && (
                      <button
                        type="button"
                        role="radio"
                        aria-checked={paymentMethod === 'stripe'}
                        className={`payment-method-btn ${paymentMethod === 'stripe' ? 'active' : ''}`}
                        onClick={() => setPaymentMethod('stripe')}
                      >
                        <span className="payment-method-btn-title">
                          💳 {c.card}
                          {isStripeSandbox && <span className="payment-sandbox-badge">{c.sandbox}</span>}
                          {!isStripeSandbox && userIsAdmin && <span className="payment-live-badge">{c.live}</span>}
                        </span>
                        <span className="payment-method-btn-hint">
                          {isStripeSandbox ? c.stripeTest : c.stripeSecure}
                        </span>
                        {paymentMethod === 'stripe' && (
                          <span className="payment-method-selected-badge">{c.selected}</span>
                        )}
                      </button>
                      )}
                      {showPayPalPayment && (
                      <button
                        type="button"
                        role="radio"
                        aria-checked={paymentMethod === 'paypal'}
                        className={`payment-method-btn ${paymentMethod === 'paypal' ? 'active' : ''}`}
                        onClick={() => setPaymentMethod('paypal')}
                      >
                        <span className="payment-method-btn-title">
                          PayPal
                          {isPayPalSandbox && <span className="payment-sandbox-badge">{c.sandbox}</span>}
                          {!isPayPalSandbox && userIsAdmin && <span className="payment-live-badge">{c.live}</span>}
                        </span>
                        <span className="payment-method-btn-hint">
                          {isPayPalSandbox ? c.paypalHintTest : c.paypalHintLive}
                        </span>
                        {paymentMethod === 'paypal' && (
                          <span className="payment-method-selected-badge">{c.selected}</span>
                        )}
                      </button>
                      )}
                      {showEmailCart && (
                        <button
                          type="button"
                          role="radio"
                          aria-checked={paymentMethod === 'email'}
                          className={`payment-method-btn ${paymentMethod === 'email' ? 'active' : ''}`}
                          onClick={() => setPaymentMethod('email')}
                        >
                          <span className="payment-method-btn-title">📧 {c.emailMyCart}</span>
                          <span className="payment-method-btn-hint">{c.emailCartHint}</span>
                          {paymentMethod === 'email' && (
                            <span className="payment-method-selected-badge">{c.selected}</span>
                          )}
                        </button>
                      )}
                    </div>

                    {/* Admin: environment controls only for the selected provider */}
                    {userIsAdmin && paymentConfig && providerModesAvailable(normalizeAvailableProviderModes(paymentConfig)) && (
                      paymentMethod === 'stripe' || paymentMethod === 'paypal'
                    ) && (
                      <PaymentModeSwitch
                        context="checkout"
                        showSandboxHints
                        providers={paymentMethod === 'stripe' ? ['stripe'] : ['paypal']}
                        stripeMode={adminStripeMode || paymentConfig.stripeMode || paymentConfig.defaultStripeMode}
                        paypalMode={adminPayPalMode || paymentConfig.paypalMode || paymentConfig.defaultPayPalMode}
                        defaultStripeMode={paymentConfig.defaultStripeMode || paymentConfig.defaultPaymentMode}
                        defaultPayPalMode={paymentConfig.defaultPayPalMode || paymentConfig.defaultPaymentMode}
                        availableProviderModes={normalizeAvailableProviderModes(paymentConfig)}
                        paymentConfig={paymentConfig}
                        onStripeChange={handleAdminStripeModeChange}
                        onPayPalChange={handleAdminPayPalModeChange}
                        disabled={switchingPaymentMode || isProcessing}
                      />
                    )}
                  </div>
                  )}

                  {/* Stripe Payment */}
                  {showStripePayment && paymentMethod === 'stripe' && (
                    <div className="payment-provider-section payment-action-panel payment-action-panel--stripe">
                      <div className="payment-action-header">
                        <p className="payment-step-label">{c.step2Pay}</p>
                        <h4>{c.payWithCard}</h4>
                        <p className="payment-action-amount">
                          {c.totalDue} <strong>${(total / 100).toFixed(2)} CAD</strong>
                          {dualCheckoutTotal && (
                            <span style={{ display: 'block', fontSize: '0.8rem', fontWeight: 400, color: 'var(--text-secondary, #888)', marginTop: '2px' }}>
                              {fmt(c.approx, { amount: dualCheckoutTotal.secondary })}
                            </span>
                          )}
                        </p>
                        {isStripeSandbox ? (
                          <p className="payment-action-hint">
                            {c.stripeSandboxHint}
                          </p>
                        ) : (
                          <p className="payment-action-hint payment-action-hint--live">
                            {c.stripeLiveHint}
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        className="payment-btn stripe-btn"
                        onClick={handleStripePayment}
                        disabled={isProcessing || !stripe || mustVerifyBeforePay}
                      >
                        {isProcessing ? `⏳ ${c.processing}` : fmt(c.payWithCardAmount, { amount: (total / 100).toFixed(2) })}
                      </button>
                    </div>
                  )}

                  {/* PayPal Payment */}
                  {showPayPalPayment && paymentMethod === 'paypal' && (
                    <div className="payment-provider-section payment-action-panel payment-action-panel--paypal">
                      <div className="payment-action-header">
                        <p className="payment-step-label">{c.step2Pay}</p>
                        <h4>{c.payWithPaypal}</h4>
                        <p className="payment-action-amount">
                          {c.totalDue} <strong>${(total / 100).toFixed(2)} CAD</strong>
                          {dualCheckoutTotal && (
                            <span style={{ display: 'block', fontSize: '0.8rem', fontWeight: 400, color: 'var(--text-secondary, #888)', marginTop: '2px' }}>
                              {fmt(c.approx, { amount: dualCheckoutTotal.secondary })}
                            </span>
                          )}
                        </p>
                        <p className="payment-action-hint">
                          {c.paypalButtonHint}
                          {isPayPalSandbox && c.paypalSandboxHint}
                        </p>
                      </div>
                      <div className="paypal-pay-zone">
                        <p className="paypal-pay-zone-label">{c.paypalButtonLabel}</p>
                        <div id="paypal-button-container"></div>
                      </div>
                    </div>
                  )}

                  {/* Email cart — available to guests while card/PayPal are in test mode */}
                  {showEmailCart && paymentMethod === 'email' && (
                    <div className="payment-provider-section payment-action-panel payment-action-panel--email">
                      <div className="payment-action-header">
                        <p className="payment-step-label">{c.step2Send}</p>
                        <h4>{c.emailMyCart}</h4>
                        <p className="payment-action-amount">
                          {c.cartTotal} <strong>${(total / 100).toFixed(2)} CAD</strong>
                        </p>
                        <p className="payment-action-hint">
                          {userIsAdmin ? c.emailCartAdminHint : c.emailCartGuestHint}
                        </p>
                      </div>
                      {userIsAdmin && (
                        <div className="form-group cart-inquiry-admin-email">
                          <label htmlFor="cart-inquiry-test-email">
                            {c.sendTestTo} <span className="required">*</span>
                          </label>
                          <input
                            id="cart-inquiry-test-email"
                            type="email"
                            className="checkout-field-input"
                            value={adminCartTestEmail}
                            onChange={(e) => setAdminCartTestEmail(e.target.value)}
                            placeholder="your.admin@email.com"
                            autoComplete="email"
                            disabled={isSendingCartInquiry}
                          />
                          <span className="field-hint">
                            {c.testEmailHint}
                          </span>
                        </div>
                      )}
                      <label htmlFor="cart-inquiry-message" className="cart-inquiry-label">
                        {c.personalMessage} <span className="optional-hint">{c.optional}</span>
                      </label>
                      <textarea
                        id="cart-inquiry-message"
                        className="cart-inquiry-message"
                        rows={4}
                        maxLength={2000}
                        placeholder={c.personalMessagePlaceholder}
                        value={cartInquiryMessage}
                        onChange={(e) => setCartInquiryMessage(e.target.value)}
                        disabled={isSendingCartInquiry}
                      />
                      <button
                        type="button"
                        className="payment-btn test-cart-inquiry-btn"
                        onClick={handleSendCartToPaul}
                        disabled={isSendingCartInquiry || !items.length}
                      >
                        {isSendingCartInquiry ? c.sending : c.sendMyCart}
                      </button>
                      {cartInquiryReference && (
                        <p className="cart-inquiry-success">
                          {fmt(c.sentRef, { ref: cartInquiryReference })}
                          {userIsAdmin ? c.sentAdmin : c.sentGuest}
                        </p>
                      )}
                    </div>
                  )}

                  {isProcessing && (
                    <div className="processing-overlay">
                      <LoadingSpinner size="medium" message={c.processingPayment} />
                    </div>
                  )}

                  <div className="step-navigation">
                    <button
                      type="button"
                      onClick={handlePreviousStep}
                      className="previous-step-btn"
                      disabled={isProcessing}
                    >
                      {getIcon('back')} {c.backToShipping}
                    </button>
                  </div>
                </div>
              )}
            </form>
          </div>

          <div className="order-summary">
            {/* Your Cart Section */}
            <div className="cart-summary-section">
              <h3 className="checkout-summary-heading">
                {cartUi.yourCart}
                <span className="checkout-summary-heading-meta">
                  {fmt(cartUi.itemsCount, { count: items.reduce((sum, i) => sum + i.quantity, 0) })}
                </span>
              </h3>

              <div className="cart-items-list">
                {items.map(item => {
                  const product = products.find(p => p.id === item.id);
                  const imageUrl = getProductImage(product);
                  const itemPrice = getLineUnitPriceCents(item);

                  // Determine tax info for display
                  let taxLabel = '';
                  const currentCountry = resolveCountryCode(shippingAddress);
                  const currentProvince = getStateProvinceValue(shippingAddress, currentCountry);

                  if (currentCountry === 'CA') {
                    // Check if product is tax-exempt
                    if (product?.tax_type === 'exempt' || item.tax_type === 'exempt') {
                      taxLabel = c.taxExempt;
                    }
                    // Check if it's GST-only (books and other eligible items)
                    else if (item.tax_type === 'gst' || product?.tax_type === 'gst' ||
                      (product?.category && isPointOfSaleRebateEligible(product.category, product.name, 'CA'))) {
                      taxLabel = 'GST 5%';
                    }
                    // Otherwise use province-specific tax  
                    else if (currentProvince) {
                      const rate = getTaxRate('CA', currentProvince);
                      if (rate > 0) {
                        let label = 'Tax';
                        // HST Provinces
                        if (['ON', 'NB', 'NL', 'NS', 'PE'].includes(currentProvince)) label = 'HST';
                        // GST Provinces
                        else if (['AB', 'NT', 'NU', 'YT'].includes(currentProvince)) label = 'GST';
                        // GST + PST/QST Provinces
                        else label = 'GST/PST';

                        const rateDisplay = (rate * 100).toFixed(rate % 1 === 0 ? 0 : 1).replace('.0', '');
                        taxLabel = `${label} ${rateDisplay}%`;
                      } else {
                        // Fall fallback or exempt
                        taxLabel = c.taxExempt;
                      }
                    } else {
                      taxLabel = c.taxAtCheckout;
                    }
                  } else if (currentCountry === 'US' || currentCountry === 'INTL') {
                    taxLabel = c.noTax;
                  }

                  return (
                    <div key={item.cartItemId || item.id} className={`cart-item-row ${item.isBundle ? 'cart-item-row--bundle' : ''}`}>
                      <div className="item-image-container">
                        <img
                          src={imageUrl}
                          alt={item.name}
                          style={{
                            width: '52px',
                            height: '52px',
                            objectFit: 'cover',
                            borderRadius: '4px',
                            border: '1px solid var(--border-color)'
                          }}
                        />
                      </div>

                      <div className="item-info">
                        <div style={{ fontWeight: '600', marginBottom: '0.25rem', lineHeight: '1.2' }}>
                          {item.name}
                          {item.isCheckoutPromo && (
                            <span style={{
                              background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                              color: '#ffffff',
                              padding: '2px 6px',
                              borderRadius: '10px',
                              fontSize: '0.7rem',
                              fontWeight: '600',
                              marginLeft: '6px',
                              display: 'inline-block',
                              verticalAlign: 'middle'
                            }}>
                              {cartUi.freeGift}
                            </span>
                          )}
                          {isDigitalOrVirtualLine(item) && (
                            <span
                              title={cartUi.notShippedTitle}
                              style={{
                                background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
                                color: '#ffffff',
                                padding: '2px 6px',
                                borderRadius: '10px',
                                fontSize: '0.7rem',
                                fontWeight: '600',
                                marginLeft: '6px',
                                display: 'inline-block',
                                verticalAlign: 'middle'
                              }}
                            >
                              {cartUi.notShipped}
                            </span>
                          )}
                          {item.isBundle && (
                            <span style={{
                              background: 'linear-gradient(135deg, var(--color-primary, #4a90e2) 0%, #5b9ceb 100%)',
                              color: '#ffffff',
                              padding: '2px 6px',
                              borderRadius: '10px',
                              fontSize: '0.7rem',
                              fontWeight: '600',
                              marginLeft: '6px',
                              display: 'inline-block',
                              verticalAlign: 'middle'
                            }}>
                              📦 {cartUi.bundle}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                          {product?.description ? (
                            <div style={{ marginBottom: '0.25rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '150px' }}>
                              {product.description}
                            </div>
                          ) : (
                            <div style={{ marginBottom: '0.25rem' }}>{product?.type || c.itemFallback}</div>
                          )}
                          <div style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', fontWeight: '500' }}>
                            {taxLabel}
                          </div>
                          {(item.selectedSize || item.selectedColor) && (
                            <div className="item-variants" style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
                              {item.selectedSize && <span style={{ marginRight: '8px' }}>{cartUi.size} <strong>{item.selectedSize}</strong></span>}
                              {item.selectedColor && <span>{cartUi.color} <strong>{item.selectedColor}</strong></span>}
                            </div>
                          )}
                          {item.isBundle && item.bundleProducts && item.bundleProducts.length > 0 && (
                            <BundleContentsToggle
                              bundleProducts={item.bundleProducts}
                              listIdPrefix={`checkout-bundle-${item.cartItemId || item.id}`}
                            />
                          )}
                          {item.isBundle && item.bundleDiscount > 0 && item.originalPrice > 0 && (
                            <div style={{
                              color: 'var(--primary-color)',
                              fontWeight: '600',
                              fontSize: '0.75rem',
                              marginTop: '2px'
                            }}>
                              🎉 {fmt(cartUi.saveOff, {
                                amount: `$${(((item.originalPrice - getLineUnitPriceCents(item)) * item.quantity) / 100).toFixed(2)}`,
                                pct: item.bundleDiscount,
                              })}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="item-pricing" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.5rem' }}>
                        <div style={{ fontWeight: '600' }}>${((itemPrice * item.quantity) / 100).toFixed(2)}</div>

                        {item.isBundle || item.isCheckoutPromo ? (
                          <div className="qty-control" style={{
                            display: 'flex',
                            alignItems: 'center',
                            opacity: 0.7,
                            fontSize: '0.9rem'
                          }}>
                            <span>{fmt(cartUi.qty, { n: item.quantity })}</span>
                          </div>
                        ) : (
                        <div className="qty-control" style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          background: 'var(--background-darker, #f3f4f6)',
                          borderRadius: '4px',
                          padding: '2px 6px',
                          border: '1px solid var(--border-color)'
                        }}>
                          <button
                            type="button"
                            onClick={() => updateQuantity(item.cartItemId || item.id, item.quantity - 1)}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              cursor: 'pointer',
                              padding: '0 4px',
                              fontWeight: 'bold',
                              color: 'var(--text-primary)',
                              fontSize: '1rem',
                              lineHeight: '1'
                            }}
                            aria-label={cartUi.decreaseQty}
                          >{getIcon('minus')}</button>
                          <span style={{ fontSize: '0.9rem', minWidth: '1ch', textAlign: 'center' }}>{item.quantity}</span>
                          <button
                            type="button"
                            onClick={() => updateQuantity(item.cartItemId || item.id, item.quantity + 1)}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              cursor: 'pointer',
                              padding: '0 4px',
                              fontWeight: 'bold',
                              color: 'var(--text-primary)',
                              fontSize: '1rem',
                              lineHeight: '1'
                            }}
                            aria-label={cartUi.increaseQty}
                          >{getIcon('plus')}</button>
                        </div>
                        )}

                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '4px' }}>
                          <button
                            type="button"
                            onClick={() => removeItem(item.cartItemId || item.id)}
                            className="cart-item-remove"
                            aria-label={item.isCheckoutPromo ? cartUi.removeGift : cartUi.removeItem}
                            title={item.isCheckoutPromo ? cartUi.removeGiftFromCart : cartUi.removeFromCart}
                          >
                            {getIcon('delete')}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="financial-summary-section">
              <h3 className="checkout-summary-heading checkout-summary-heading--totals">{c.orderSummary}</h3>

              <div className="checkout-summary-rows">
              <div className="checkout-summary-row">
                <span className="checkout-summary-row-label">{cartUi.subtotal.replace(':', '')}</span>
                <span className="checkout-summary-row-value">${(subtotal / 100).toFixed(2)}</span>
              </div>

              <div className="checkout-summary-row">
                <span className="checkout-summary-row-label">
                  {c.shipping}
                  <div className="info-icon">
                    i
                    <span className="tooltip-text" style={{ width: '280px', textAlign: 'left' }}>
                      <div style={{ borderBottom: '1px solid #555', paddingBottom: '4px', marginBottom: '4px', fontWeight: 'bold' }}>
                        {c.shippingCalc}
                      </div>
                      {shipping.info?.isFlatRate && shipping.info?.flatRateBreakdown ? (
                        <>
                          {shipping.info?.totalWeight != null && shipping.info.totalWeight > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                              <span>{c.estWeight}</span>
                              <span>{shipping.info.totalWeight.toFixed(2)} kg</span>
                            </div>
                          )}
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <span>{c.rateType}</span>
                            <span>{fmt(c.flatRate, { dest: shipping.info.flatRateBreakdown.destKey })}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <span>{c.baseShippingHandling}</span>
                            <span>${(shipping.info.flatRateBreakdown.handlingCents / 100).toFixed(2)}</span>
                          </div>
                          {shipping.info.flatRateBreakdown.perItemBreakdown?.length > 0 && (
                            <div style={{ marginBottom: '2px', fontSize: '0.9em' }}>{c.postageAdjustment}</div>
                          )}
                          {shipping.info.flatRateBreakdown.perItemBreakdown?.map((b, idx) => (
                            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px', fontSize: '0.9em' }}>
                              <span>{formatFlatRateItemLabel(b)}</span>
                              <span>${((b.totalCents || 0) / 100).toFixed(2)}</span>
                            </div>
                          ))}
                          {flatRateBreakdownHasTierChanges(shipping.info.flatRateBreakdown.perItemBreakdown) && (
                            <div style={{ fontSize: '0.75em', color: '#aaa', marginTop: '2px', fontStyle: 'italic' }}>
                              {c.rateBandNote}
                            </div>
                          )}
                        </>
                      ) : shipping.info?.isWeightBased ? (
                        <>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <span>{c.totalWeight}</span>
                            <span>{shipping.info?.totalWeight?.toFixed(2)}kg</span>
                          </div>
                          {shipping.info?.shippingBandLabel && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                              <span>{c.band}</span>
                              <span style={{ fontWeight: '600' }}>{shipping.info.shippingBandLabel}</span>
                            </div>
                          )}
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <span>{c.rateType}</span>
                            <span>{c.weightBased}</span>
                          </div>
                        </>
                      ) : (
                        <div style={{ marginBottom: '4px' }}>{c.fixedFlat}</div>
                      )}
                      {shipping.info?.fuelBreakdown ? (
                        <div style={{ borderTop: '1px solid #555', paddingTop: '4px', marginTop: '4px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px', fontSize: '0.85em' }}>
                            <span>{c.baseRate}</span>
                            <span>${(shipping.info.fuelBreakdown.baseCents / 100).toFixed(2)}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px', fontSize: '0.85em' }}>
                            <span>{c.fuelSurcharge}</span>
                            <span>${(shipping.info.fuelBreakdown.fuelCents / 100).toFixed(2)}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', borderTop: '1px solid #444', paddingTop: '3px', marginTop: '3px' }}>
                            <span>{c.shippingCost}</span>
                            <span>{shipping.cost === 0 ? c.free : `$${(shipping.cost / 100).toFixed(2)}`}</span>
                          </div>
                        </div>
                      ) : (
                        <div style={{ borderTop: '1px solid #555', paddingTop: '4px', marginTop: '4px', display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
                          <span>{c.shippingCost}</span>
                          <span>{shipping.cost === 0 ? c.free : `$${(shipping.cost / 100).toFixed(2)}`}</span>
                        </div>
                      )}
                    </span>
                  </div>
                </span>
                <div className="checkout-summary-row-value-block">
                  <span className="checkout-summary-row-value">
                    {!shipping.isCalculated ? (
                      <span className="checkout-summary-row-hint">Calculated at next step</span>
                    ) : (
                      shipping.cost === 0 ? 'FREE' : `$${(shipping.cost / 100).toFixed(2)}`
                    )}
                  </span>
                  {shipping.isCalculated && (
                    <div className="checkout-summary-row-sub">
                      {shipping.info?.isFlatRate
                        ? (shipping.info?.shippingBandLabel || `FLAT RATE${shipping.info?.totalWeight > 0 ? ` (${shipping.info.totalWeight.toFixed(2)} kg)` : ''}`)
                        : shipping.info?.isWeightBased
                          ? (shipping.info?.shippingBandLabel || `WEIGHT BASED (${shipping.info?.totalWeight?.toFixed(2)}kg)`)
                          : 'FLAT RATE'}
                    </div>
                  )}
                </div>
              </div>

              {/* One download note in the summary — only for digital albums; skip if already shown on contact step */}
              {cartHasDigital && currentStep !== 1 && (
                <p className="checkout-summary-note checkout-summary-note--info">
                  {cartIsMixedDigitalPhysical
                    ? <>📥 Digital downloads are <strong>not shipped</strong>. Shipping covers physical items only.</>
                    : <>📥 Digital downloads are <strong>not shipped</strong> — delivered online only.</>}
                </p>
              )}

              {shipping.isCalculated && shipping.cost > 0 && (
                <p className="checkout-summary-note">
                  Estimated shipping — if overcharged, it's refunded.
                </p>
              )}

              <div className="checkout-summary-row">
                <span className="checkout-summary-row-label">
                  Estimated Tax
                  <div className="info-icon">
                      i
                      <span className="tooltip-text" style={{ width: '250px', textAlign: 'left' }}>
                        <div style={{ borderBottom: '1px solid #555', paddingBottom: '4px', marginBottom: '4px', fontWeight: 'bold' }}>
                          {resolveCountryCode(shippingAddress) === 'CA'
                            ? (getStateProvinceValue(shippingAddress, 'CA') || 'Canadian') + ' Tax Rules'
                            : 'Tax Breakdown'}
                        </div>
                        {tax.breakdown && tax.breakdown.length > 0 ? (
                          tax.breakdown.map((t, i) => (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                              <span>{t.description}</span>
                              <span>${(t.amount / 100).toFixed(2)}</span>
                            </div>
                          ))
                        ) : resolveCountryCode(shippingAddress) === 'CA' ? (
                          <div>Enter your province to calculate Canadian tax</div>
                        ) : (
                          <div>No sales tax on US / international orders</div>
                        )}
                        <div style={{ borderTop: '1px solid #555', paddingTop: '4px', marginTop: '4px', display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
                          <span>Total Tax</span>
                          <span>${(tax.amount / 100).toFixed(2)}</span>
                        </div>
                      </span>
                    </div>
                </span>
                <span className="checkout-summary-row-value">
                  {!tax.isCalculated ? (
                    <span className="checkout-summary-row-hint">Calculating...</span>
                  ) : tax.amount === 0 && resolveCountryCode(shippingAddress) !== 'CA' ? (
                    <span className="checkout-summary-row-hint">No tax</span>
                  ) : (
                    `$${(tax.amount / 100).toFixed(2)}`
                  )}
                </span>
              </div>

              {/* USA Tariff Section - Only show for USA orders */}
              {tariff && tariff.isApplicable && (
                <>
                <div className="checkout-summary-row">
                  <span className="checkout-summary-row-label">
                    <span style={{ marginRight: '4px' }}>🇺🇸</span>
                    U.S. Import Tariff
                    <div className="info-icon">
                        i
                        <span className="tooltip-text" style={{ width: '280px', textAlign: 'left' }}>
                          <div style={{ borderBottom: '1px solid #555', paddingBottom: '4px', marginBottom: '4px', fontWeight: 'bold' }}>
                            U.S. Tariff Calculation
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <span>Tariff Rate:</span>
                            <span>{((tariff.rate || 0) * 100).toFixed(0)}%</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <span>Exemption:</span>
                            <span>${(tariff.exemptionThreshold || 0).toFixed(2)} USD</span>
                          </div>
                          {tariff.tariffableAmountUsd > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', color: 'var(--primary-color)' }}>
                              <span>Tariffable Amount:</span>
                              <span>${tariff.tariffableAmountUsd.toFixed(2)} USD</span>
                            </div>
                          )}
                          <div style={{ borderTop: '1px solid #555', paddingTop: '4px', marginTop: '4px', fontSize: '0.8rem' }}>
                            Tariffs apply to Canada-to-U.S. shipments per U.S. Customs regulations.
                          </div>
                        </span>
                      </div>
                  </span>
                  <span className="checkout-summary-row-value">${(tariff.amount / 100).toFixed(2)}</span>
                </div>
                  {tariff.message && (
                    <p className="checkout-summary-note">{tariff.message}</p>
                  )}
                </>
              )}

              {transactionFee > 0 && (
                <div className="checkout-summary-row">
                  <span className="checkout-summary-row-label">
                    Payment processing fee ({transactionFeeInfo.pct}%)
                    <span className="checkout-summary-row-hint">Covers Stripe / PayPal card fees</span>
                  </span>
                  <span className="checkout-summary-row-value">${(transactionFee / 100).toFixed(2)}</span>
                </div>
              )}

              <div className="checkout-summary-total">
                <span>Total (CAD)</span>
                <span className="checkout-summary-total-value">
                  ${(total / 100).toFixed(2)} CAD
                  {dualCheckoutTotal && (
                    <span className="checkout-summary-total-secondary">
                      {dualCheckoutTotal.secondary}
                    </span>
                  )}
                </span>
              </div>
              {checkoutCurrencyNote && (
                <p className="checkout-summary-currency-note">
                  {checkoutCurrencyNote}
                </p>
              )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <AdminMailOrderModal
        isOpen={showMailOrderModal}
        onClose={() => {
          setShowMailOrderModal(false);
          setMailOrderDraft(null);
        }}
        initialDraft={mailOrderDraft}
        onOrderCreated={handleMailOrderCreatedFromCheckout}
        emailInvoiceForOrder={handleEmailMailOrderInvoiceFromCheckout}
      />
    </div>
  );
};

export default Checkout; 